/**
 * The Entries panel for customer and vendor formulas: the balance date the
 * formula's year and month give, and the saved view the rows are shown
 * through — reversed signs, or ageing buckets and basis of their own. A view
 * is stored on the server and named in the cell as BIFROST.ENTRIES's last
 * argument, so every recalc, and every colleague, sees the same rows. The
 * company's defaults (Portal) decide the Ledger figures; a view never does.
 */

export interface AgeingSettings {
  limits: number[];
  basis: 'dueDate' | 'documentDate';
}

export interface ViewSettings {
  reverseSign: boolean;
  ageing?: AgeingSettings;
}

export interface SavedView {
  id: string;
  name: string;
  settings: ViewSettings;
}

export interface ViewsClient {
  list(formulaId: string): Promise<{ defaults: { ageing: AgeingSettings }; views: SavedView[] } | null>;
  create(formulaId: string, name: string, settings: ViewSettings): Promise<{ ok: true } | { ok: false; error: string }>;
}

/** The functions types whose rows carry a balance date and ageing. */
export function hasViews(functionType: string): boolean {
  return functionType === 'ar' || functionType === 'ap';
}

/** "30, 60, 90" as bucket limits, or why it is not. */
export function parseLimits(text: string): number[] | { error: string } {
  const parts = text.split(/[,;\s]+/).filter(Boolean);
  if (parts.length === 0) return { error: 'Give at least one limit, e.g. 30, 60, 90' };
  const limits = parts.map(Number);
  if (limits.some(n => !Number.isInteger(n) || n < 1)) return { error: 'Limits are whole days, 1 or more' };
  if (limits.some((n, i) => i > 0 && n <= (limits[i - 1] as number))) return { error: 'Each limit must be larger than the one before' };
  if (limits.length > 12) return { error: 'At most 12 limits' };
  return limits;
}

/** The bucket names the limits give, as the server names them. */
export function bucketLabels(limits: number[]): string[] {
  const labels = ['Not due'];
  let from = 1;
  for (const limit of limits) {
    labels.push(`${from}–${limit}`);
    from = limit + 1;
  }
  labels.push(`${from}+`);
  return labels;
}

/** The last day of the month the year and month name, as dd.mm.yyyy, or '' while incomplete. */
export function balanceDateLabel(year: string, month: string): string {
  const y = Number(year.trim());
  const m = Number(month.trim());
  if (!Number.isInteger(y) || y < 1900 || !Number.isInteger(m) || m < 1 || m > 12) return '';
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return `${String(last).padStart(2, '0')}.${String(m).padStart(2, '0')}.${y}`;
}

const escape = (s: string) => s.replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string));

export class EntriesViewPanel {
  private views: SavedView[] = [];
  private defaults: AgeingSettings = { limits: [30, 60, 90, 180, 365], basis: 'dueDate' };
  private selected: string | null = null;
  private status = '';

  constructor(
    private container: HTMLElement,
    private client: ViewsClient,
    private formulaId: string,
    private onViewChange: (viewName: string | null) => void
  ) {}

  async load(): Promise<void> {
    const listed = await this.client.list(this.formulaId);
    if (listed) {
      this.views = listed.views;
      this.defaults = listed.defaults.ageing;
    }
    this.render();
  }

  /** Called by the builder when the year or month changes. */
  setBalanceDate(label: string): void {
    const el = this.container.querySelector('#evp-balance-date');
    if (el) el.textContent = label || 'Enter year and month';
  }

  private render(): void {
    const options = [`<option value="">Company default</option>`]
      .concat(this.views.map(v => `<option value="${escape(v.name)}"${v.name === this.selected ? ' selected' : ''}>${escape(v.name)}</option>`))
      .join('');
    this.container.innerHTML = `
      <div class="evp">
        <div class="evp-row"><span class="evp-label">Balance date</span><span id="evp-balance-date" class="evp-value">Enter year and month</span></div>
        <label class="evp-label" for="evp-view">View</label>
        <select id="evp-view" class="evp-input">${options}</select>
        <details class="evp-new">
          <summary>New view</summary>
          <label class="evp-check"><input type="checkbox" id="evp-reverse" /> Reverse signs</label>
          <label class="evp-label" for="evp-basis">Ageing from</label>
          <select id="evp-basis" class="evp-input">
            <option value="dueDate"${this.defaults.basis === 'dueDate' ? ' selected' : ''}>Due date</option>
            <option value="documentDate"${this.defaults.basis === 'documentDate' ? ' selected' : ''}>Invoice date</option>
          </select>
          <label class="evp-label" for="evp-limits">Ageing buckets, upper limits in days</label>
          <input id="evp-limits" class="evp-input" value="${this.defaults.limits.join(', ')}" />
          <p id="evp-buckets" class="evp-hint">${escape(bucketLabels(this.defaults.limits).join(' · '))}</p>
          <label class="evp-label" for="evp-name">Name</label>
          <input id="evp-name" class="evp-input" maxlength="60" placeholder="e.g. Ageing 15 days" />
          <button class="ms-Button" id="evp-save" type="button"><span class="ms-Button-label">Save view</span></button>
        </details>
        <p id="evp-status" class="evp-hint" role="status">${escape(this.status)}</p>
      </div>`;
    this.attach();
  }

  private attach(): void {
    const select = this.container.querySelector<HTMLSelectElement>('#evp-view');
    select?.addEventListener('change', () => {
      this.selected = select.value || null;
      this.onViewChange(this.selected);
    });
    const limits = this.container.querySelector<HTMLInputElement>('#evp-limits');
    limits?.addEventListener('input', () => {
      const parsed = parseLimits(limits.value);
      const hint = this.container.querySelector('#evp-buckets');
      if (hint) hint.textContent = Array.isArray(parsed) ? bucketLabels(parsed).join(' · ') : parsed.error;
    });
    this.container.querySelector('#evp-save')?.addEventListener('click', () => { void this.save(); });
  }

  private async save(): Promise<void> {
    const value = (id: string) => this.container.querySelector<HTMLInputElement | HTMLSelectElement>(id)?.value ?? '';
    const name = value('#evp-name').trim();
    const limits = parseLimits(value('#evp-limits'));
    if (!name) return this.setStatus('Give the view a name');
    if (!Array.isArray(limits)) return this.setStatus(limits.error);
    const reverseSign = this.container.querySelector<HTMLInputElement>('#evp-reverse')?.checked ?? false;
    const settings: ViewSettings = { reverseSign, ageing: { limits, basis: value('#evp-basis') === 'documentDate' ? 'documentDate' : 'dueDate' } };
    const saved = await this.client.create(this.formulaId, name, settings);
    if (!saved.ok) return this.setStatus(saved.error);
    this.selected = name;
    this.status = `Saved "${name}" for everyone in the company`;
    await this.load();
    this.onViewChange(name);
  }

  private setStatus(text: string): void {
    this.status = text;
    const el = this.container.querySelector('#evp-status');
    if (el) el.textContent = text;
  }
}
